import { Box, Button, Card, Checkbox, Container, Flex, Heading, Link, Text } from "@radix-ui/themes";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import logo from "../assets/logo.png";
import Footer from "../components/shared/Footer";
import { useAuth } from "../contexts/AuthContext";

// Stored verbatim in marketing_consents.consent_text. Keep this constant in
// sync with the label rendered next to the checkbox below.
export const MARKETING_CONSENT_TEXT =
    "I agree to receive product updates, offers, and marketing emails from Desktop Commander. I can unsubscribe at any time.";

const PRIVACY_POLICY_URL = "https://legal.desktopcommander.app/#/privacy_desktop_commander_app";

/**
 * Marketing Consent Page
 *
 * Transient step shown once, immediately after signup (email or Google), before
 * the user reaches the final destination. Records the consent decision for the
 * already-authenticated user, then resumes the original flow by navigating back
 * to '/' with the in-progress query params intact.
 */
export default function MarketingConsentPage() {
    const { recordMarketingConsent } = useAuth();
    const navigate = useNavigate();
    const [marketingConsent, setMarketingConsent] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    const handleFinish = async () => {
        setSubmitting(true);
        // A row is created only when opted in; either way the server clears the
        // pending marker so this page is not shown again.
        await recordMarketingConsent(marketingConsent, MARKETING_CONSENT_TEXT);
        // Resume the original flow — AuthPage's dispatcher takes over from here.
        navigate(`/${window.location.search}`, {
            replace: true,
        });
    };

    return (
        <Container
            size="2"
            p="4"
            style={{
                minHeight: "100vh",
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
                // Optical centering — keep placement consistent with the login screen.
                paddingBottom: "12vh",
            }}
        >
            <Card
                size="4"
                style={{
                    width: "100%",
                }}
            >
                <Flex
                    direction="column"
                    gap="5"
                    align="center"
                    style={{
                        textAlign: "center",
                        padding: "24px 8px",
                    }}
                >
                    <img
                        src={logo}
                        alt="Logo"
                        style={{
                            width: 64,
                            height: 64,
                            objectFit: "contain",
                        }}
                    />

                    <Heading size="6">Your account is ready</Heading>

                    <Text as="label" size="3">
                        <Flex
                            as="span"
                            gap="2"
                            align="start"
                            style={{
                                maxWidth: 380,
                                textAlign: "left",
                            }}
                        >
                            <Checkbox size="3" checked={marketingConsent} onCheckedChange={(checked) => setMarketingConsent(checked === true)} />
                            <span>{MARKETING_CONSENT_TEXT}</span>
                        </Flex>
                    </Text>

                    <Box
                        style={{
                            width: "100%",
                            maxWidth: 380,
                        }}
                    >
                        <Button
                            size="3"
                            highContrast
                            loading={submitting}
                            onClick={handleFinish}
                            style={{
                                width: "100%",
                            }}
                        >
                            Launch Desktop Commander
                        </Button>
                    </Box>

                    <Text size="1" color="gray">
                        By continuing, you acknowledge Desktop Commander&rsquo;s{" "}
                        <Link href={PRIVACY_POLICY_URL} target="_blank" rel="noopener noreferrer">
                            Privacy Policy
                        </Link>
                        .
                    </Text>
                </Flex>
            </Card>
            <Footer border={false} />
        </Container>
    );
}
